/**
 * What the publish is actually doing, read from WebRTC's own statistics.
 *
 * `qualityLimitationReason`, which `R-HOST-11`'s bandwidth clause reads, is one field of this
 * report, and everything here comes from the same snapshot. It is worth
 * showing because a host presenting for two hours has no other way to know their stream is
 * degrading: the picture on their own screen is the *camera*, not what left the machine, and it
 * looks perfect while the upstream falls apart.
 *
 * ## What these figures are, and what they are not
 *
 * These are **upstream** measurements: what this browser encoded and sent, and what IVS told it
 * about what arrived. They are not `R-VID-9`'s *what viewers are actually seeing, including the
 * true delay* — that needs a second, subscribing connection to the stage, which is the stream
 * monitor (`monitor/Monitor.tsx`). Nor is
 * `roundTripMs` glass-to-glass latency (`R-VID-3`): it is the network time between this machine and
 * IVS, which is a *component* of that figure and typically the smaller half.
 *
 * Saying so precisely matters, because these numbers will be read during a show and quoted
 * afterwards. A host who reports "the latency was 40ms" from this panel has measured the first hop
 * of a path that also has an encode, a server, a network and a decode in it.
 *
 * ## Why the arithmetic is here
 *
 * Bitrate and loss are **deltas** — the report gives cumulative counters, so a rate only exists
 * between two samples — and the health verdict is a judgement. Both are the kind of thing that is
 * wrong in a way nobody notices on a screen, so they live in a pure function with a test rather
 * than inside the sampling loop.
 */

/** One reading, and enough of the previous one to compute a rate. */
export interface PublishSnapshot {
  /** `performance.now()` when the report was taken. */
  at: number;
  videoBytesSent: number | undefined;
  audioBytesSent: number | undefined;
  packetsSent: number | undefined;
  packetsLost: number | undefined;
}

export type StreamHealth = "good" | "fair" | "poor" | "unknown";

export interface PublishStats {
  /** What is being encoded right now, which after adaptation may be below what was requested. */
  width: number | undefined;
  height: number | undefined;
  fps: number | undefined;
  /** Kilobits per second, video and audio separately. Undefined on the first sample. */
  videoKbps: number | undefined;
  audioKbps: number | undefined;
  /** What the congestion controller believes the connection could carry. */
  availableKbps: number | undefined;
  /** Round trip to IVS in milliseconds. **Not** glass-to-glass latency — see above. */
  roundTripMs: number | undefined;
  /** Percentage of packets IVS reported lost, over the interval rather than cumulative. */
  lossPercent: number | undefined;
  /** WebRTC holding the encoder back, and by what: `bandwidth` or `cpu`. */
  limitedBy: string | undefined;
  /** True when the media path runs through a TURN relay, which is worth knowing on a bad network. */
  relayed: boolean | undefined;
  /** Key frames encoded so far. A climbing count during a stable shot means recovery from loss. */
  keyFrames: number | undefined;
  health: StreamHealth;
  /** Carried forward so the next call can compute its rates. */
  snapshot: PublishSnapshot;
}

/**
 * A minimal view of `RTCStatsReport`: a map of entries with a `type`. Typed loosely on purpose —
 * the real report's members vary by browser and by version, and every field read below is checked.
 */
export type StatsReport = {
  forEach: (callback: (entry: Record<string, unknown>) => void) => void;
};

function num(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

/** Bits per second between two cumulative byte counters, as kbps. */
function rateKbps(
  bytes: number | undefined,
  previousBytes: number | undefined,
  seconds: number,
): number | undefined {
  if (bytes === undefined || previousBytes === undefined || seconds <= 0)
    return undefined;
  const delta = bytes - previousBytes;
  // A counter that went backwards means the connection was rebuilt underneath us — a rejoin, or a
  // renegotiation. There is no rate to report for that interval; there will be one next time.
  if (delta < 0) return undefined;
  return Math.round((delta * 8) / seconds / 1000);
}

/**
 * The verdict, in the order a host would care about it.
 *
 * Loss first, because it is the thing viewers see. `bandwidth` limitation second: the encoder is
 * being held back, which is a soft picture rather than a broken one — `R-VID-11`'s trade, working as
 * intended, but worth surfacing before somebody blames the camera. Round trip last and generously,
 * because a host on the other side of the world is not a fault.
 */
function verdict(
  lossPercent: number | undefined,
  limitedBy: string | undefined,
  roundTripMs: number | undefined,
): StreamHealth {
  if (
    lossPercent === undefined &&
    limitedBy === undefined &&
    roundTripMs === undefined
  ) {
    return "unknown";
  }
  if ((lossPercent ?? 0) >= 5) return "poor";
  if ((roundTripMs ?? 0) >= 400) return "poor";
  if ((lossPercent ?? 0) >= 1) return "fair";
  if (limitedBy === "bandwidth" || limitedBy === "cpu") return "fair";
  if ((roundTripMs ?? 0) >= 200) return "fair";
  return "good";
}

/**
 * Reads one report into a snapshot, using the previous one for anything rate-shaped.
 *
 * `remote-inbound-rtp` is the interesting half and the one that is easy to miss: it is **IVS
 * reporting back what it received**, so its `packetsLost` and `roundTripTime` are measurements of
 * the real path rather than of this machine's intentions. Where it is absent — early in a
 * connection, or on a browser that does not provide it — the selected candidate pair's round trip
 * stands in.
 */
export function readPublishStats(
  report: StatsReport | undefined,
  previous: PublishSnapshot | undefined,
  at: number,
): PublishStats {
  let width: number | undefined;
  let height: number | undefined;
  let fps: number | undefined;
  let videoBytesSent: number | undefined;
  let audioBytesSent: number | undefined;
  let packetsSent: number | undefined;
  let packetsLost: number | undefined;
  let roundTripMs: number | undefined;
  let candidateRoundTripMs: number | undefined;
  let availableKbps: number | undefined;
  let limitedBy: string | undefined;
  let keyFrames: number | undefined;
  let relayed: boolean | undefined;

  const candidates = new Map<string, Record<string, unknown>>();
  let selectedLocalCandidate: string | undefined;

  report?.forEach((entry) => {
    /**
     * **Simulcast means several of these, and they have to be combined rather than overwritten.**
     *
     * The publisher sends layers (`R-VID-11` — a viewer on a poor connection needs something lower
     * to drop to), so the report carries one `outbound-rtp` per layer. Assigning on each pass
     * reports whichever layer the browser happens to list last — often the smallest — and a
     * bitrate counting one layer out of three, so a host is shown `360 × 640` while a 720p layer
     * is going out beside it.
     *
     * So: **resolution and frame rate from the largest layer that is actually sending, and bytes
     * and packets summed across all of them.** A layer that congestion control has switched off
     * reports `active: false` and is excluded from the resolution — the point of the figure is what
     * viewers can get, and a disabled layer offers nothing.
     */
    if (entry.type === "outbound-rtp" && entry.kind === "video") {
      videoBytesSent = (videoBytesSent ?? 0) + (num(entry.bytesSent) ?? 0);
      packetsSent = (packetsSent ?? 0) + (num(entry.packetsSent) ?? 0);
      keyFrames = Math.max(keyFrames ?? 0, num(entry.keyFramesEncoded) ?? 0);

      const layerWidth = num(entry.frameWidth);
      const layerHeight = num(entry.frameHeight);
      const sending = entry.active !== false;
      if (
        sending &&
        layerWidth !== undefined &&
        layerHeight !== undefined &&
        layerWidth * layerHeight > (width ?? 0) * (height ?? 0)
      ) {
        width = layerWidth;
        height = layerHeight;
        fps = num(entry.framesPerSecond);
      }

      /**
       * Any layer being held back is a limitation worth reporting, and `none` never overwrites a
       * reason — with three layers, the smallest is usually unconstrained and would otherwise
       * silence the fact that the largest is not.
       */
      if (
        typeof entry.qualityLimitationReason === "string" &&
        entry.qualityLimitationReason !== "none"
      ) {
        limitedBy = entry.qualityLimitationReason;
      }
    }
    if (entry.type === "outbound-rtp" && entry.kind === "audio") {
      audioBytesSent = (audioBytesSent ?? 0) + (num(entry.bytesSent) ?? 0);
    }
    // Summed and maximised for the same reason: one of these per layer, and loss on any of them is
    // loss. The worst round trip is the honest one to show.
    if (entry.type === "remote-inbound-rtp" && entry.kind === "video") {
      const lost = num(entry.packetsLost);
      if (lost !== undefined) packetsLost = (packetsLost ?? 0) + lost;
      const trip = num(entry.roundTripTime);
      if (trip !== undefined) {
        roundTripMs = Math.max(roundTripMs ?? 0, Math.round(trip * 1000));
      }
    }
    if (entry.type === "candidate-pair" && entry.nominated === true) {
      const trip = num(entry.currentRoundTripTime);
      if (trip !== undefined) candidateRoundTripMs = Math.round(trip * 1000);
      const bitrate = num(entry.availableOutgoingBitrate);
      if (bitrate !== undefined) availableKbps = Math.round(bitrate / 1000);
      if (typeof entry.localCandidateId === "string") {
        selectedLocalCandidate = entry.localCandidateId;
      }
    }
    if (entry.type === "local-candidate" && typeof entry.id === "string") {
      candidates.set(entry.id, entry);
    }
  });

  if (selectedLocalCandidate !== undefined) {
    const chosen = candidates.get(selectedLocalCandidate);
    if (chosen && typeof chosen.candidateType === "string") {
      relayed = chosen.candidateType === "relay";
    }
  }

  const seconds = previous ? (at - previous.at) / 1000 : 0;
  const videoKbps = rateKbps(videoBytesSent, previous?.videoBytesSent, seconds);
  const audioKbps = rateKbps(audioBytesSent, previous?.audioBytesSent, seconds);

  /**
   * Loss over the interval, not since the connection began. A cumulative figure is worse than
   * useless on a long show: one bad minute in the first half hour would sit on the screen for the
   * rest of it, and a host would learn to ignore the number.
   */
  let lossPercent: number | undefined;
  if (
    packetsSent !== undefined &&
    packetsLost !== undefined &&
    previous?.packetsSent !== undefined &&
    previous.packetsLost !== undefined
  ) {
    const sent = packetsSent - previous.packetsSent;
    const lost = packetsLost - previous.packetsLost;
    if (sent > 0 && lost >= 0) {
      lossPercent = Math.round((lost / (sent + lost)) * 1000) / 10;
    } else if (sent > 0) {
      lossPercent = 0;
    }
  }

  const trip = roundTripMs ?? candidateRoundTripMs;

  return {
    width,
    height,
    fps: fps === undefined ? undefined : Math.round(fps),
    videoKbps,
    audioKbps,
    availableKbps,
    roundTripMs: trip,
    lossPercent,
    // `none` never reaches this — see the note above on why it must not overwrite a real reason.
    limitedBy,
    relayed,
    keyFrames,
    health: verdict(lossPercent, limitedBy, trip),
    snapshot: { at, videoBytesSent, audioBytesSent, packetsSent, packetsLost },
  };
}
